import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Grid,
  CircularProgress,
  Divider,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActionArea
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Category as CategoryIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { getMainCategories } from '../../services/categoryService';
import { Category } from '../../utils/types';

// استخدام صورة افتراضية من Cloudinary
const placeholderImage = 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg';

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        // تحميل الفئات الرئيسية
        const mainCategories = await getMainCategories();
        
        // تصفية الفئات النشطة فقط
        const activeCategories = mainCategories.filter(cat => cat.isActive);
        setCategories(activeCategories);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('حدث خطأ أثناء تحميل الفئات');
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      {/* فتات الخبز */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link component={RouterLink} to="/" color="inherit">
          الرئيسية
        </Link>
        <Typography color="text.primary">
          الفئات
        </Typography>
      </Breadcrumbs>

      {/* عنوان الصفحة */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CategoryIcon sx={{ mr: 1 }} />
          تصفح الفئات
        </Typography>
        <Typography variant="body1" color="text.secondary">
          اختر من بين مجموعة متنوعة من الفئات لتصفح منتجاتنا
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* عرض الفئات */}
      {categories.length === 0 ? (
        <Alert severity="info">لا توجد فئات متاحة حالياً</Alert>
      ) : (
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                }
              }}>
                <CardActionArea 
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={category.imageUrl || placeholderImage}
                    alt={category.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      {category.name}
                    </Typography>
                    {category.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                        {category.description.length > 100
                          ? `${category.description.substring(0, 100)}...`
                          : category.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default CategoriesPage;
